import { useEffect, useState } from 'react';
import type { ServiceLandingData } from '../../../mocks/service-landings';

interface SLHeroProps {
  data: ServiceLandingData;
  onCtaClick: () => void;
}

export default function SLHero({ data, onCtaClick }: SLHeroProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const scrollToForm = () => {
    const el = document.querySelector('#kontaktny-formular');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-zinc-950">
      {/* Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.10) 0%, transparent 60%)' }}
        />
        <div
          className="absolute -bottom-20 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 65%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.022]"
          style={{
            backgroundImage: 'linear-gradient(rgba(249,115,22,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute bottom-0 inset-x-0 h-36 bg-gradient-to-t from-zinc-950 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-14 xl:gap-20">

          {/* Left */}
          <div className="flex-1 lg:max-w-[52%]">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 bg-zinc-900/80 border border-orange-500/20 rounded-full px-3.5 py-1.5 mb-7"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 0.55s ease, transform 0.55s ease',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${data.icon} ${data.accent} text-sm`} />
              </div>
              <span className="text-orange-300 text-xs sm:text-sm font-medium">{data.hero.badge}</span>
            </div>

            {/* Headline */}
            <div
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(22px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
                transitionDelay: '0.1s',
              }}
            >
              <h1 className="text-[2.2rem] sm:text-4xl md:text-5xl lg:text-[3.2rem] xl:text-[3.6rem] font-black text-white leading-[1.06] tracking-tight mb-4">
                {data.hero.headline}
                <br />
                <span className={`text-transparent bg-clip-text bg-gradient-to-r ${data.accentFrom} ${data.accentTo}`}>
                  {data.hero.headlineAccent}
                </span>
              </h1>
            </div>

            {/* Subheadline */}
            <p
              className="text-zinc-400 text-base sm:text-lg max-w-lg mb-9 leading-relaxed"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
                transitionDelay: '0.22s',
              }}
            >
              {data.hero.subheadline}
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row gap-3 mb-9"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
                transitionDelay: '0.33s',
              }}
            >
              <button
                onClick={onCtaClick}
                className="group relative inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative">{data.hero.ctaPrimary}</span>
                <i className="ri-arrow-right-line relative transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 border border-zinc-700/60 hover:border-orange-500/35 text-zinc-300 hover:text-white font-medium px-7 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base bg-zinc-900/50 hover:bg-orange-500/5"
              >
                Dozvedieť sa viac
                <i className="ri-arrow-down-line text-sm text-zinc-500" />
              </button>
            </div>

            {/* Trust indicators */}
            <div
              className="flex flex-wrap items-center gap-x-5 gap-y-2.5"
              style={{
                opacity: visible ? 1 : 0,
                transition: 'opacity 0.65s ease',
                transitionDelay: '0.44s',
              }}
            >
              {[
                { icon: 'ri-shield-check-line', label: 'Konzultácia zadarmo' },
                { icon: 'ri-time-line', label: 'Rýchla odpoveď' },
                { icon: 'ri-customer-service-2-line', label: 'Podpora po odovzdaní' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1.5">
                  <i className={`${item.icon} text-orange-500 text-sm`} />
                  <span className="text-zinc-500 text-xs font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - image */}
          <div
            className="hidden lg:block flex-shrink-0 w-full lg:w-[46%] xl:w-[44%] mt-10 lg:mt-0"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(28px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
              transitionDelay: '0.4s',
            }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-3xl" />
              <div className="relative w-full h-[460px] rounded-3xl overflow-hidden border border-zinc-800/60">
                <img
                  src={data.hero.image}
                  alt={data.hero.badge}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-transparent" />
              </div>
              {/* Floating accent */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl bg-zinc-900 border border-zinc-800 flex flex-col items-center justify-center gap-1">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${data.icon} text-2xl ${data.accent}`} />
                </div>
                <span className="text-zinc-300 text-[10px] font-semibold text-center leading-tight px-2">boostio</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="hidden sm:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 cursor-pointer"
        onClick={scrollToForm}
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease', transitionDelay: '1.1s' }}
      >
        <span className="text-zinc-700 text-[10px] tracking-widest uppercase">scroll</span>
        <div className="w-5 h-8 border border-zinc-700/60 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-1.5 rounded-full bg-orange-400" style={{ animation: 'driftUp 1.6s ease-in-out infinite' }} />
        </div>
      </div>
    </section>
  );
}
